<template>
  <div ref="htmlContainer" class="map-container">
    <div ref="viewport" class="viewport">
      <div
        ref="mapContent"
        class="map-content"
        v-html="MapSVG"
      />
    </div>
    <button
      v-if="buttons.left"
      class="rc-btn rc-btn__map rc-btn__map-left"
      @click.prevent.stop="handleMapMove(100, 0)"
    />
    <button
      v-if="buttons.right"
      class="rc-btn rc-btn__map rc-btn__map-right"
      @click.prevent.stop="handleMapMove(-100, 0)"
    />
    <button
      v-if="buttons.up"
      class="rc-btn rc-btn__map rc-btn__map-up"
      @click.prevent.stop="handleMapMove(0, 100)"
    />
    <button
      v-if="buttons.bottom"
      class="rc-btn rc-btn__map rc-btn__map-bottom"
      @click.prevent.stop="handleMapMove(0, -100)"
    />
    <!-- Прицел неподвижен в центре, карта ездит под ним: палец
         закрывает ровно тот стенд, по которому целишься -->
    <div
      v-if="aiming"
      class="map-aim"
      aria-hidden="true"
    >
      <span class="map-aim__ring" />
      <span class="map-aim__dot" />
    </div>
    <button
      class="rc-btn rc-btn__map rc-btn__map-zoom-plus"
      @click="handleMapZoom(1)"
    >+</button>
    <button
      class="rc-btn rc-btn__map rc-btn__map-zoom-minus"
      @click="handleMapZoom(-1)"
    >-</button>
  </div>
</template>

<script>
import { drawRoute, clearRoute } from '@/views/pages/MapView/navigation/drawRoute';
import {
  setStandEmpty,
  standNumber,
  normalizeStandNumber,
  detectEmptyStandClass,
  EDITOR_CLASS,
} from '@/views/pages/MapView/standState';

/** Что прицел считает целью: те же якоря, что несёт граф. */
const AIMABLE_ID = /^(stand|poi|door|navpoint)-/;

/** Пометка «сейчас под прицелом». Вид описан ниже, в стилях. */
const AIMED_CLASS = 'aimed';

/**
 * Приближение шагает умножением, а не прибавкой.
 *
 * Прибавка в 0.2 давала 20% на первом нажатии и 2% на сороковом — кнопка
 * как будто переставала работать, а весь диапазон проходился за 46 нажатий.
 * Умножение даёт одинаковые 25% на любом приближении и те же 1 → 10 за 11
 * нажатий.
 */
const ZOOM_STEP = 1.25;

/**
 * Чувствительность колеса. Одна «ступенька» мыши — это обычно deltaY 100,
 * то есть примерно те же 22%, что и у кнопки.
 */
const WHEEL_SENSITIVITY = 0.002;

/** Дальше приближать нечего: стенды и так крупнее экрана. */
const MAX_SCALE = 10;

export default {
  name: 'MapItemView',

  props: {
    map: {
      type: Object,
      default: null,
    },
    title: {
      type: String,
      default: null,
    },
    standList: {
      type: Array,
      default: () => [],
    },
    standInfo: {
      type: Object,
      default: null,
    },
    startStandId: {
      type: String,
      default: null,
    },
    aiming: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['click-stand', 'ready'],

  data() {
    return {
      stands: new Map(),
      vpWidth: 0,
      vpHeight: 0,
      isDragging: false,
      originWidth: null,
      originHeight: null,
      startX: 0,
      startX_2: 0,
      startY: 0,
      startY_2: 0,
      offsetX: 0,
      offsetY: 0,
      shiftX: 0,
      shiftY: 0,
      scale: 1.0,

      // Отложенная подсветка найденного стенда — см. cancelHighlightTimer
      highlightTimer: null,

      // Каким словом эта карта называет пустой стенд — см. standState.js
      emptyClass: EDITOR_CLASS,

      buttons: {
        left: false,
        right: false,
        bottom: false,
        up: false,
      },
    };
  },

  computed: {
    MapSVG() {
      return this.map?.svg_map ?? '';
    },
  },

  watch: {
    aiming(value) {
      if (!value) this.clearAim();
    },
  },

  mounted() {
    // Намеренно мимо data(): элемент карты не должен становиться реактивным
    this.aimedNode = null;

    this.$nextTick(() => {
      this.initMap();
    });
  },

  beforeUnmount() {
    this.removeListeners();
    this.cancelHighlightTimer();
  },

  methods: {
    // ─── Инициализация ───────────────────────────────────────
    initMap() {
      const { viewport } = this.$refs;
      const { mapContent } = this.$refs;
      if (!viewport || !mapContent) return;

      // const standIds = this.standList
      // .map((s) => (typeof s === 'string' ? s : s.id ?? s.num_stand ?? ''));

      // Привязка кликов к стендам
      mapContent.querySelectorAll('g.stand').forEach((stand) => {
        const numStand = stand.id;
        // if (standIds.includes(numStand)) {
        // Занятость стенда проставляет applyDirectory, когда придёт
        // справочник: до него неизвестно, за кем стенд числится.
        this.stands.set(numStand, stand);

        stand.addEventListener('click', () => {
          if (this.isDragging) return;
          if (!stand.classList.contains('active')) {
            this.highlightStand(stand.id);
            console.log(stand);
            this.$emit('click-stand', stand.id);
          }
        });
        // }
      });

      // Вычисление начальных размеров
      const svgEl = mapContent.children[0];
      this.emptyClass = detectEmptyStandClass(svgEl);
      if (svgEl) {
        this.originWidth = svgEl.clientWidth;
        this.originHeight = svgEl.clientHeight;
      }

      this.offsetX = (viewport.offsetWidth - mapContent.offsetWidth * this.scale) / 2;
      this.offsetY = (viewport.offsetHeight - mapContent.offsetHeight * this.scale) / 2;
      // shift считает updateTransform ниже — он единственный, кто это делает

      const vpRect = viewport.getBoundingClientRect();
      this.vpWidth = vpRect.width;
      this.vpHeight = vpRect.height;

      // Слушатели
      this.addListeners();
      this.updateTransform();

      // Автопоказ стенда
      if (this.startStandId) {
        setTimeout(() => {
          this.showStand(this.startStandId);
        }, 550);
      }

      // Карта разобрана: снаружи можно строить маршрут
      this.$emit('ready');
    },

    addListeners() {
      const vp = this.$refs.viewport;
      if (!vp) return;

      vp.addEventListener('mousedown', this.handleMouseDown);
      vp.addEventListener('mousemove', this.handleMouseMove);
      vp.addEventListener('mouseup', this.handleMouseUp);
      vp.addEventListener('wheel', this.handleWheel, { passive: false });

      vp.addEventListener('touchstart', this.handleTouchStart);
      vp.addEventListener('touchmove', this.handleTouchMove);
      vp.addEventListener('touchend', this.handleTouchEnd);
    },

    removeListeners() {
      const vp = this.$refs.viewport;
      if (!vp) return;

      vp.removeEventListener('mousedown', this.handleMouseDown);
      vp.removeEventListener('mousemove', this.handleMouseMove);
      vp.removeEventListener('mouseup', this.handleMouseUp);
      vp.removeEventListener('wheel', this.handleWheel);

      vp.removeEventListener('touchstart', this.handleTouchStart);
      vp.removeEventListener('touchmove', this.handleTouchMove);
      vp.removeEventListener('touchend', this.handleTouchEnd);
    },

    // ─── Touch ───────────────────────────────────────────────
    handleTouchStart(e) {
      if (e.touches[0]) {
        this.isDragging = true;
        this.startX = e.touches[0].clientX - this.offsetX;
        this.startY = e.touches[0].clientY - this.offsetY;
      }
      if (e.touches[1]) {
        e.preventDefault();
        this.isDragging = false;
        this.startX_2 = e.touches[1].clientX - this.offsetX;
        this.startY_2 = e.touches[1].clientY - this.offsetY;
      }
    },

    handleTouchMove(e) {
      e.stopPropagation();
      e.preventDefault();

      if (e.touches.length === 1) {
        if (!this.isDragging) return;
        this.offsetX = e.touches[0].clientX - this.startX;
        this.offsetY = e.touches[0].clientY - this.startY;
        this.updateTransform();
      }
      if (e.touches.length === 2) {
        this.pinchZoom(e.touches[0], e.touches[1]);
      }
    },

    handleTouchEnd() {
      this.isDragging = false;
    },

    pinchZoom(touch1, touch2) {
      const initialDistance = this.calculateDistance(
        { x: this.startX, y: this.startY },
        { x: this.startX_2, y: this.startY_2 },
      );
      const currentDistance = this.calculateDistance(
        {
          x: touch1.clientX - this.offsetX,
          y: touch1.clientY - this.offsetY,
        },
        {
          x: touch2.clientX - this.offsetX,
          y: touch2.clientY - this.offsetY,
        },
      );
      const diff = Math.round(initialDistance) - Math.round(currentDistance);

      if (diff !== 0 && Math.round(diff) % 10 === 0) {
        this.startX = touch1.clientX - this.offsetX;
        this.startY = touch1.clientY - this.offsetY;
        this.startX_2 = touch2.clientX - this.offsetX;
        this.startY_2 = touch2.clientY - this.offsetY;

        // Щипок шагает тем же множителем, что и кнопка, а привязкой ему
        // служит середина между пальцами — то место, которое человек и
        // растягивает.
        this.zoomAround(
          diff > 0 ? this.scale / ZOOM_STEP : this.scale * ZOOM_STEP,
          (touch1.clientX + touch2.clientX) / 2,
          (touch1.clientY + touch2.clientY) / 2,
        );
      }
    },

    calculateDistance(p1, p2) {
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      return Math.sqrt(dx * dx + dy * dy);
    },

    // ─── Mouse ───────────────────────────────────────────────
    handleMouseDown(e) {
      e.preventDefault();
      this.isDragging = true;
      this.startX = e.clientX - this.offsetX;
      this.startY = e.clientY - this.offsetY;
      this.$refs.viewport.style.cursor = 'grabbing';
      this.$refs.mapContent.classList.add('handle-drag');
    },

    handleMouseMove(e) {
      e.stopPropagation();
      e.preventDefault();
      if (!this.isDragging) return;

      this.offsetX = e.clientX - this.startX;
      this.offsetY = e.clientY - this.startY;
      this.updateTransform();
    },

    handleMouseUp() {
      this.isDragging = false;
      if (this.$refs.viewport) {
        this.$refs.viewport.style.cursor = 'grab';
      }
      if (this.$refs.mapContent) {
        this.$refs.mapContent.classList.remove('handle-drag');
      }
    },

    // ─── Управление картой ───────────────────────────────────
    handleMapMove(x, y) {
      this.offsetX += x;
      this.offsetY += y;
      this.updateTransform();
    },

    /**
     * Насколько можно отдалиться. Не число из головы, а сама карта: по
     * ширине она вписана уже при масштабе 1, поэтому упереться можно
     * только в высоту. Уменьшать сверх того, где карта видна целиком,
     * незачем — дальше идёт пустое поле вокруг неё.
     */
    minScale() {
      if (!this.originHeight || !this.vpHeight) return 1;
      return Math.min(1, this.vpHeight / this.originHeight);
    },

    /** Поставить масштаб, не выходя за пределы. */
    applyScale(next) {
      this.scale = Math.min(Math.max(this.minScale(), next), MAX_SCALE);
    },

    /**
     * Приблизить, удержав на месте одну точку экрана.
     *
     * Сам по себе масштаб растит карту от её левого верхнего угла: там
     * начало её координат, и смещение при этом не меняется. Чтобы карта
     * росла «от точки», смещение надо подвинуть на столько, насколько эта
     * точка уехала бы, — тогда под пальцем, курсором или серединой экрана
     * остаётся то же место схемы.
     *
     * @param {number} next масштаб, к которому идём
     * @param {number} screenX точка привязки в координатах экрана
     * @param {number} screenY
     */
    zoomAround(next, screenX, screenY) {
      const mc = this.$refs.mapContent;
      if (!mc) return;

      const oldScale = this.scale;
      this.applyScale(next);

      // Прямоугольник ещё старый: transform применит updateTransform ниже
      const bound = mc.getBoundingClientRect();
      const anchorX = screenX - bound.left;
      const anchorY = screenY - bound.top;
      const ratio = this.scale / oldScale;

      this.offsetX += anchorX - anchorX * ratio;
      this.offsetY += anchorY - anchorY * ratio;

      this.updateTransform();
    },

    /** @param {number} direction 1 — приблизить, -1 — отдалить. */
    handleMapZoom(direction) {
      const vp = this.$refs.viewport;
      if (!vp) return;

      // Кнопкам привязкой служит середина окна карты: человек смотрит
      // туда, и приближаться должно к тому, что он видит.
      const box = vp.getBoundingClientRect();
      this.zoomAround(
        direction > 0 ? this.scale * ZOOM_STEP : this.scale / ZOOM_STEP,
        box.left + box.width / 2,
        box.top + box.height / 2,
      );
    },

    handleWheel(e) {
      e.preventDefault();

      // Экспонента, а не прибавка: тогда одна ступенька колеса приближает
      // на одну и ту же долю, на каком бы масштабе её ни крутили. Нижний
      // предел держит applyScale — прежняя проверка «не мельче четверти
      // окна» его повторяла и потому убрана.
      this.zoomAround(this.scale * Math.exp(-e.deltaY * WHEEL_SENSITIVITY), e.clientX, e.clientY);
    },

    // ─── Transform и ограничения ─────────────────────────────
    checkOffset() {
      const contentBound = this.$refs.mapContent.getBoundingClientRect();

      const maxLeft = (this.vpWidth - contentBound.width) / 2 - contentBound.width / 2;
      const maxRight = (this.vpWidth - contentBound.width) / 2 + contentBound.width / 2;

      const maxTop = (this.vpHeight - contentBound.height) / 2 - contentBound.height / 2;
      const maxBottom = (this.vpHeight - contentBound.height) / 2 + contentBound.height / 2;

      this.offsetX = Math.max(Math.min(this.offsetX, maxRight), maxLeft);
      this.offsetY = Math.max(Math.min(this.offsetY, maxBottom), maxTop);
    },

    updateTransform() {
      // Точка трансформации у карты в центре, поэтому браузер при масштабе
      // сам уводит содержимое на (W − W·scale) / 2; shift это гасит, и
      // offsetX остаётся смещением левого края от края окна карты.
      //
      // Считаем здесь, а не у каждого способа зума. Раньше пересчёт стоял
      // только в колесе мыши, а кнопки «+/−» и щипок его не делали — shift
      // оставался от прежнего масштаба, и showStand, который считает цель
      // абсолютно, промахивался ровно на эту величину: на масштабе 1.6 это
      // 110px по горизонтали и 151 по вертикали, то есть мимо экрана.
      this.shiftX = (this.originWidth - this.originWidth * this.scale) / 2;
      this.shiftY = (this.originHeight - this.originHeight * this.scale) / 2;

      this.checkOffset();
      const mc = this.$refs.mapContent;
      if (!mc) return;
      mc.style.transform = `translate(${this.offsetX - this.shiftX}px, ${
        this.offsetY - this.shiftY
      }px) scale(${this.scale})`;
    },

    /**
     * Что сейчас под прицелом.
     *
     * Прицел стоит в середине окна карты, поэтому спрашиваем у
     * браузера, что лежит в этой точке экрана, и поднимаемся к
     * ближайшему предку с опознаваемым id. Работа идёт в экранных
     * координатах, так что CSS-трансформ контейнера пересчитывать не
     * надо: он уже учтён.
     *
     * @returns {Element|null}
     */
    aimedElement() {
      const vp = this.$refs.viewport;
      if (!vp) return null;

      const box = vp.getBoundingClientRect();
      let node = document.elementFromPoint(
        box.left + box.width / 2,
        box.top + box.height / 2,
      );

      while (node && node !== vp) {
        if (node.id && AIMABLE_ID.test(node.id)) return node;
        node = node.parentElement;
      }

      return null;
    },

    /**
     * Обновляет прицеливание: находит цель под прицелом и подсвечивает
     * её на карте. Подсветка нужна потому, что подпись в панели видит
     * не всякий, а на карте промах виден сразу.
     *
     * Класс свой, `aimed`, а не `active` от карты: карта намеренно не
     * подсвечивает пустой стенд — правило написано как
     * `.stand:not(.empty-stand).active`. Для прицела это не годится,
     * он показывает, куда наведён, а не кто там стоит; пустых стендов
     * при этом большинство.
     *
     * @returns {{id: String, label: String}|null}
     */
    updateAim() {
      const node = this.aimedElement();
      if (node === this.aimedNode) {
        return node ? { id: node.id, label: standNumber(node) || node.id } : null;
      }

      if (this.aimedNode) this.aimedNode.classList.remove(AIMED_CLASS);
      this.aimedNode = node;
      if (!node) return null;

      node.classList.add(AIMED_CLASS);
      return { id: node.id, label: standNumber(node) || node.id };
    },

    /** Снимает подсветку прицела, ничего больше не трогая. */
    clearAim() {
      if (!this.aimedNode) return;
      this.aimedNode.classList.remove(AIMED_CLASS);
      this.aimedNode = null;
    },

    /**
     * Номера всех стендов карты. Берутся из разметки, а не из графа:
     * искать стенд по номеру нужно и там, где навигации нет.
     */
    standNumbers() {
      const mc = this.$refs.mapContent;
      if (!mc) return [];

      return [...mc.querySelectorAll('g.stand')]
        .map((stand) => standNumber(stand))
        .filter((number) => number);
    },

    // ─── Занятость стендов ───────────────────────────────────
    /**
     * Помечает стенды по справочнику: за кем числится компания —
     * занят, остальные пусты. Зовётся и когда справочник приходит
     * позже карты.
     * @param {Set<String>} occupied нормализованные номера стендов
     * @returns {Number} сколько стендов оказалось занято
     */
    applyDirectory(occupied) {
      const mc = this.$refs.mapContent;
      if (!mc || !occupied) return 0;

      let taken = 0;
      mc.querySelectorAll('g.stand').forEach((stand) => {
        const isEmpty = !occupied.has(normalizeStandNumber(standNumber(stand)));
        setStandEmpty(stand, isEmpty, this.emptyClass);
        if (!isEmpty) taken += 1;
      });

      return taken;
    },

    // ─── Маршрут ─────────────────────────────────────────────
    svgRoot() {
      const mc = this.$refs.mapContent;
      return mc ? mc.querySelector('svg') : null;
    },

    showRoute(route) {
      return drawRoute(this.svgRoot(), route);
    },

    hideRoute() {
      return clearRoute(this.svgRoot());
    },

    // ─── Подсветка стендов ────────────────────────────────────

    /**
     * Найденный стенд загорается не сразу: сначала гаснут остальные, и
     * только через 600 мс он становится оранжевым. Таймер живёт в поле,
     * потому что его приходится отменять — иначе он дорисует подсветку
     * уже после того, как её сняли, и она останется висеть на карте.
     */
    cancelHighlightTimer() {
      if (!this.highlightTimer) return;
      clearTimeout(this.highlightTimer);
      this.highlightTimer = null;
    },

    /** Снять подсветку целиком: и оранжевую с найденного, и серую с прочих. */
    clearHighlight() {
      this.cancelHighlightTimer();

      const mc = this.$refs.mapContent;
      if (!mc) return;

      mc.querySelectorAll('g.stand').forEach((stand) => {
        stand.classList.remove('active', 'no-active', 'animation');
      });
    },

    highlightStand(standId, noactive = false) {
      const mc = this.$refs.mapContent;
      if (!mc) return null;

      // Предыдущий поиск мог оставить незавершённый таймер: без отмены он
      // через свои 600 мс подсветит уже неактуальный стенд.
      this.cancelHighlightTimer();

      let targetStand = null;

      mc.querySelectorAll('g.stand').forEach((stand) => {
        stand.classList.remove('active', 'no-active', 'animation');
        if (noactive) {
          stand.classList.add('no-active');
        }
        if (stand.id === standId) {
          targetStand = stand;
          this.highlightTimer = setTimeout(() => {
            this.highlightTimer = null;
            stand.classList.remove('no-active');
            stand.classList.add('active', 'animation');
          }, 600);
        }
      });

      return targetStand;
    },

    emptyStand(standId) {
      const mc = this.$refs.mapContent;
      if (mc) {
        mc.querySelectorAll('g.stand').forEach((stand) => {
          stand.classList.remove('active', 'no-active', 'animation');
          if (stand.id === standId) {
            setStandEmpty(stand, true, this.emptyClass);
          }
        });
      }
    },

    showStand(standId) {
      // console.log('showStand', standId);
      const stand = this.highlightStand(`stand-${standId}`, true);
      if (!stand) return;

      const vpRect = this.$refs.viewport.getBoundingClientRect();
      const mcRect = this.$refs.mapContent.getBoundingClientRect();
      const standRect = stand.getBoundingClientRect();

      const viewportCenterX = vpRect.width / 2;
      const viewportCenterY = vpRect.height / 2;

      const standCenterX = standRect.left - mcRect.left + standRect.width / 2;
      const standCenterY = standRect.top - mcRect.top + standRect.height / 2;

      this.offsetX = viewportCenterX - standCenterX;
      this.offsetY = viewportCenterY - standCenterY;

      this.updateTransform();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/style/variables.scss';

.map-container {
  position: relative;
  grid-column: start / end;
  grid-row: start / end;
  // background-color: #f0f0f0;

  .viewport {
    width: 100%;
    height: 100%;
    overflow: hidden;
    position: relative;
    cursor: default;
  }

  .map-content {
    position: absolute;
    width: 100%;
    transition: transform 0.5s ease-out; /* Плавное перемещение */
    // will-change: transform; /* Оптимизация для анимаций */

    &.handle-drag {
      transition: transform 0.1s ease-out;
    }

    & > * {
      display: block;
      // max-width: 100%;
      // max-height: 100%;
    }
    & > svg {
      pointer-events: auto;
    }
  }
}
.rc-btn {
  &__map {
    position: absolute;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    // Тот же розовый, что у кнопок зума на старой карте (`index.vue`):
    // обе карты у клиента открыты, и разъезжаться им незачем.
    border: 2px solid $color-accent-legacy;
    background-color: $color-base;
    color: $color-accent-legacy;
    font-size: 24px;
    line-height: 1em;
    font-weight: 700;
  }

  // Карта занимает обе строки сетки, а панель снизу ложится поверх неё.
  // Поэтому кнопки поднимаются на высоту панели: --map-bottom-inset
  // ставит страница, и только когда панель выше, чем эти отступы уже
  // перекрывают. Нет панели — ноль, и кнопки стоят где стояли.
  &__map-zoom-plus {
    bottom: calc(168px + var(--map-bottom-inset, 0px));
    right: 10px;
  }
  &__map-zoom-minus {
    bottom: calc(122px + var(--map-bottom-inset, 0px));
    right: 10px;
  }
}
// ИСКЛЮЧЕНИЕ из правила «вид задаёт карта».
//
// Состояния стенда карта описывает сама, и цвета мы не трогаем. Но
// «сейчас под прицелом» карта не знает вовсе: такого класса в её
// таблице стилей нет, а `active` она намеренно не применяет к пустым
// стендам — их у нас большинство. Поэтому вид описан здесь, и цвет
// взят тот же, которым карта подсвечивает выбранное.
//
// Стоит предложить команде редактора добавить это состояние в формат:
// тогда описание уедет в карту, как и остальные, и уйдёт отсюда.
//
// Селекторы с двумя классами на группе — чтобы перебить
// `.empty-stand .stand-shape`: у карты оно идёт позже в документе.
//
// Обводка достаётся всем, заливка — только занятым. Пустой стенд
// остаётся серым: заливка цветом читалась бы как «тут кто-то есть», а
// там никого нет. Обводки хватает, чтобы показать, куда наведено.
//
// Цвет здесь — не из темы клиента, а из самой карты. SVG приходит с
// сервера один и тот же для всех клиентов и подсвечивает выбранный
// стенд вот этими значениями (`.stand.active` в его таблице стилей).
// Возьми мы персиковый акцент Интерткани — рядом оказались бы два
// разных оранжевых, и «под прицелом» читалось бы как чужое состояние.
$map-active-stroke: #d76535;
$map-active-fill: #fadbce;

:deep(.stand.aimed .stand-shape),
:deep(.poi.aimed .poi-shape) {
  stroke: $map-active-stroke;
  stroke-width: 2;
}
:deep(.stand:not(.empty-stand).aimed .stand-shape),
:deep(.poi.aimed .poi-shape) {
  fill: $map-active-fill;
}
// Подпись тоже уходит в акцент, иначе номер занятого стенда остаётся
// синим посреди оранжевого. У пустого она серая и такой остаётся — по
// той же причине, по какой серой остаётся заливка.
:deep(.stand:not(.empty-stand).aimed .stand-label),
:deep(.poi.aimed .poi-label) {
  fill: $map-active-stroke;
}
:deep(.door.aimed .door-conture) {
  stroke: $map-active-stroke;
}

.map-aim {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  pointer-events: none;

  &__ring,
  &__dot {
    position: absolute;
    border-radius: 50%;
    transform: translate(-50%, -50%);
  }

  // Оттенком светлее акцента: тем же цветом карта подсвечивает стенд
  // под прицелом, и кольцо не должно с ним сливаться
  &__ring {
    width: 44px;
    height: 44px;
    border: 2px solid lighten($map-active-stroke, 8%);
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.8);
  }

  &__dot {
    width: 6px;
    height: 6px;
    background: lighten($map-active-stroke, 8%);
  }
}
</style>
